import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type QueueRow, type Task } from '@/api/client'
import { AttachmentStrip } from '@/components/Attachments'
import { TaskDrawer } from '@/components/TaskDrawer'
import { approvalProgress, othersApproving } from '@/components/PeoplePicker'
import { Empty, ErrorNote, Spinner, StatusChip, Tag } from '@/components/ui'
import { useAuth } from '@/lib/auth'

export function Approvals() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const [open, setOpen] = useState<Task | null>(null)
  const [rejecting, setRejecting] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [err, setErr] = useState<unknown>(null)

  const q = useQuery({ queryKey: ['queue'], queryFn: () => api.get<QueueRow[]>('/approvals/queue') })
  // Defaults to your own, because being asked to decide is a job and being able to
  // cover for your team is a standing permission - and the job should be what opens.
  const [tab, setTab] = useState<'mine' | 'team'>('mine')
  // One approver's seat, or "" for all of them. Cleared whenever the tab changes: a
  // name with work on one tab usually has none on the other, so keeping it would
  // land somebody on an empty list they never asked for.
  const [person, setPerson] = useState('')

  // ?ref=147 - where an approval notification's link lands. The row is highlighted
  // rather than opened in the drawer: the drawer shows history, and the buttons that
  // decide the thing are on the row itself, which is what they came to press.
  const [params, setParams] = useSearchParams()
  const wanted = Number(params.get('ref') || 0) || null
  useEffect(() => {
    if (!wanted) return
    // Onto the tab it is actually on, first. A link that lands on the other tab
    // scrolls to a row that is not rendered, so the approver arrives at an empty
    // screen having been told there was something to look at.
    const row = q.data?.find((r) => r.task.ref === wanted)
    if (row) { setTab(row.whose); setPerson('') }
    // After the paint, because switching tab above is what renders the row - looking
    // for it in this same tick finds the list it was just moved out of.
    const frame = requestAnimationFrame(() => {
      document.getElementById(`appr-${wanted}`)
        ?.scrollIntoView({ block: 'center' })
    })
    // Dropped from the URL once used, so a refresh or a shared link does not keep
    // singling out a task that may since have been decided.
    const t = setTimeout(() => setParams({}, { replace: true }), 4000)
    return () => { cancelAnimationFrame(frame); clearTimeout(t) }
  }, [wanted, q.data, setParams])

  const done = () => {
    setErr(null); setRejecting(null); setReason('')
    qc.invalidateQueries({ queryKey: ['queue'] })
    qc.invalidateQueries({ queryKey: ['queue-count'] })
    qc.invalidateQueries({ queryKey: ['tasks'] })
    qc.invalidateQueries({ queryKey: ['day'] })
  }
  const act = useMutation({
    mutationFn: (m: { id: string; approve: boolean; reason?: string }) =>
      m.approve ? api.post(`/approvals/${m.id}/approve`)
                : api.post(`/approvals/${m.id}/reject`, { reason: m.reason }),
    onSuccess: done, onError: setErr,
  })

  if (q.isLoading) return <Spinner label="Loading your queue" />
  if (q.error) return <ErrorNote error={q.error} />
  const all = q.data ?? []
  // The server says which is which - `whose` - so the web and both bots cannot
  // disagree about what "yours" means.
  const mine = all.filter((r) => r.whose === 'mine')
  const team = all.filter((r) => r.whose === 'team')
  // Only worth two tabs when there is something in the second one. An approver with
  // nobody beneath them has one queue, and a tab strip over a single list is
  // furniture.
  const split = team.length > 0
  const inTab = !split ? all : tab === 'mine' ? mine : team

  // Whose seat is holding it up. The chat gained this with the one-door change and
  // the web should not be the surface without it - an Owner covering thirty-eight
  // approvals across nine people could read every name and ask for none of them.
  //
  // Built from the rows in view rather than from the roster: an option that filters
  // to nothing is a dead end, and the only names worth offering are the ones
  // actually sitting on something.
  const seats = (() => {
    const found = new Map<string, string>()
    for (const r of inTab)
      for (const p of r.task.people ?? [])
        if (p.kind === 'APPROVER') found.set(p.user_id, p.name)
    return [...found].sort((a, b) => a[1].localeCompare(b[1]))
  })()
  // The APPROVER's seat, not who did the work. The queue exists to find what is
  // holding things up; filtering on the responsible side answers "what did Ravi
  // submit", which is a different question and looks identical from the outside.
  const rows = person
    ? inTab.filter((r) => (r.task.people ?? [])
        .some((p) => p.kind === 'APPROVER' && p.user_id === person))
    : inTab

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      {/* Two queues, not one list. An Owner's own three approvals used to sit among
          forty they were merely able to act on, and nothing said which were which -
          so "Waiting on you" told them they were the bottleneck for work that was
          actually sitting with somebody else. */}
      {split && (
        <div className="flex items-center gap-2 mb-3">
          {([['mine', 'Your approvals', mine.length],
             ['team', "Your team's", team.length]] as const).map(([k, label, n]) => (
            <button key={k} type="button" onClick={() => { setTab(k); setPerson('') }}
                    aria-pressed={tab === k}
                    className={`px-3 h-8 rounded-md border text-[12.5px] ${
                      tab === k ? 'bg-primary text-primary-on border-primary font-medium'
                                : 'bg-surface border-rule text-ink-2 hover:text-ink'}`}>
              {label} <span className="mono opacity-75">{n}</span>
            </button>
          ))}
        </div>
      )}

      {/* Only where there is more than one seat to choose between. Over a single
          approver it is a control whose every setting shows the same list. */}
      {seats.length > 1 && (
        <div className="flex items-center gap-2 mb-3">
          <label className="label mb-0" htmlFor="appr-seat">Waiting on</label>
          <select id="appr-seat" className="field w-56" value={person}
                  onChange={(e) => setPerson(e.target.value)}>
            <option value="">Anybody ({inTab.length})</option>
            {seats.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
          {person && (
            <button type="button" className="btn btn-sm"
                    onClick={() => setPerson('')}>Clear</button>
          )}
        </div>
      )}

      <div className="card mb-4 px-4 py-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <b className="text-[13px]">
          {split && tab === 'team' ? 'Waiting on your team' : 'Waiting on you'}
        </b>
        <span className="mono text-xs text-ink-3">
          {rows.length} task{rows.length === 1 ? '' : 's'}
          {rows.length ? ` · oldest ${Math.max(...rows.map((r) => r.waiting_days))} day(s)` : ''}
        </span>
        <span className="flex-1" />
        {/* Ageing is prominent because PENDING_APPROVAL never carries forward, so
            this queue is the only place a stalled approval shows. */}
        <span className="text-[11.5px] text-ink-3">
          These never carry forward — they age here until decided.
        </span>
      </div>

      {rows.length === 0 ? (
        <Empty title={split && tab === 'team' ? 'Nothing waiting on your team'
                                              : 'Nothing waiting for you'}
               hint="Approvals will appear here as tasks are completed." />
      ) : (
        <div className="space-y-2.5">
          {rows.map(({ task, waiting_days, overdue, whose }) => (
            <div key={task.task_id} id={`appr-${task.ref}`}
                 className={`card p-4 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-start ${
                   overdue ? 'border-l-[3px] border-l-pend' : ''} ${
                   wanted === task.ref ? 'ring-2 ring-primary' : ''}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 mb-1">
                  <span className="mono text-[13px] text-primary font-medium">#{task.ref}</span>
                  <StatusChip status={task.status} approvalStatus={task.approval_status} />
                  {task.carry_forward_count > 0 && <Tag>carried {task.carry_forward_count}×</Tag>}
                </div>
                <h4 className="text-[14.5px] font-semibold tracking-tight">{task.task}</h4>
                <p className="text-[12.3px] text-ink-3 mt-0.5">
                  {task.company} · completed by {task.responsible_person}
                  {/* Only when it is not yours: on your own queue every row would say
                      your own name, which is noise. */}
                  {approvalProgress(task) && (
                    <> · {approvalProgress(task)}</>
                  )}
                  {/* The other approvers, named one by one. This tested the
                      `approver_user_id` mirror - the first approver and nothing more
                      - and then printed the joined `approver` string, so the second
                      approver on a task was told it was "with" a list that included
                      themselves. `people` carries them separately, which is what it
                      is for. */}
                  {othersApproving(task, me?.user_id).length > 0 && (
                    // "with" on your own row - they are deciding alongside you. On a
                    // row you are only covering, none of them is you, so the honest
                    // word is who it was actually asked of.
                    <> · {whose === 'mine' ? 'with' : 'to be approved by'}{' '}
                      <b className="font-semibold text-ink-2">
                      {othersApproving(task, me?.user_id).join(', ')}</b></>
                  )}
                </p>
                {task.remarks && (
                  <p className="text-[12.3px] text-ink-2 mt-2 bg-paper border border-rule rounded px-2.5 py-1.5">
                    {task.remarks}
                  </p>
                )}
                {/* Directly under the remark, above the buttons: the point of
                    attachments is that the approver looks at them BEFORE deciding,
                    so they belong on the path to the button, not behind
                    "Open history". Renders nothing when there is nothing. */}
                <AttachmentStrip task={task} />
              </div>
              {/* Full width and below the task on a phone - which is where an approver
                  arriving from a notification link actually reads this. */}
              <div className="w-full sm:w-[172px] shrink-0 space-y-1.5">
                <div className={`mono text-[11.5px] text-left sm:text-right ${overdue ? 'text-pend-ink font-semibold' : 'text-ink-3'}`}>
                  waiting {waiting_days} day{waiting_days === 1 ? '' : 's'}
                </div>
                {rejecting === task.task_id ? (
                  <>
                    <textarea autoFocus rows={3} value={reason}
                              onChange={(e) => setReason(e.target.value)}
                              placeholder="Why? Sent to them word for word."
                              className="w-full rounded-md border border-rej/40 px-2 py-1.5 text-xs" />
                    <button className="btn btn-bad btn-sm w-full justify-center"
                            disabled={reason.trim().length < 10 || act.isPending}
                            onClick={() => act.mutate({ id: task.task_id, approve: false, reason })}>
                      Send rejection
                    </button>
                    <button className="btn btn-sm w-full justify-center"
                            onClick={() => { setRejecting(null); setReason('') }}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-good w-full justify-center" disabled={act.isPending}
                            onClick={() => act.mutate({ id: task.task_id, approve: true })}>
                      Approve
                    </button>
                    <button className="btn btn-bad w-full justify-center"
                            onClick={() => setRejecting(task.task_id)}>
                      Reject with reason
                    </button>
                    <button className="btn btn-sm w-full justify-center"
                            onClick={() => setOpen(task)}>Open history</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {open && <TaskDrawer task={open} onClose={() => setOpen(null)} />}
    </>
  )
}
